import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API_URL } from '../config';

/**
 * useDayLocks(year, month, userId)
 * - PHPの action=load (読み込み) と action=saveDayLock (保存) に対応
 */
export function useDayLocks(year, month, userId) {
  const [lockedDays, setLockedDays] = useState([]); // ["YYYY-MM-DD", ...]
  const [loading, setLoading] = useState(false);
  
  // ★重要: APIのURLを環境に合わせて確認してください
  const apiBaseRef = useRef(API_URL);
  const cancelSourceRef = useRef(null);

  // normalize helper
  const normalizeDates = (arr = []) => {
    if (!Array.isArray(arr)) return [];
    const normalized = arr
      .map((d) => {
        if (!d) return null;
        const s = String(d).trim();
        return s.split(" ")[0]; // "YYYY-MM-DD"
      })
      .filter(Boolean);
    return Array.from(new Set(normalized));
  };

  const fetchLocks = useCallback(async () => {
    if (!year || !month || !userId) {
      setLockedDays([]);
      return;
    }

    if (cancelSourceRef.current) {
      cancelSourceRef.current.cancel("new request started");
    }
    const source = axios.CancelToken.source();
    cancelSourceRef.current = source;

    setLoading(true);
    try {
      // ★変更点1: action=getDayLocks ではなく action=load を使用
      // (修正版PHPでは load で rows と locks の両方を返す設計にしたため)
      const res = await axios.get(`${apiBaseRef.current}?action=load`, {
        cancelToken: source.token,
        params: { year, month, user_id: userId }, // user_id (snake_case) に注意
      });

      if (res && res.data && Array.isArray(res.data.locks)) {
        const normalized = normalizeDates(res.data.locks);
        setLockedDays(normalized);
        // console.log("取得したロック日:", normalized);
      } else {
        // load失敗、またはロックなし
        setLockedDays([]);
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log("fetchLocks cancelled:", err.message);
      } else {
        console.error("ロック情報の取得に失敗:", err);
        setLockedDays([]);
      }
    } finally {
      setLoading(false);
      cancelSourceRef.current = null;
    }
  }, [year, month, userId]);

  useEffect(() => {
    fetchLocks();
    return () => {
      if (cancelSourceRef.current) {
        cancelSourceRef.current.cancel("component unmounted");
      }
    };
  }, [fetchLocks]);

  // toggleLock
  const toggleLock = async (fullDate, lock) => {
    if (!userId) return;

    // optimistic update
    const prev = lockedDays;
    const newLocked = lock
      ? Array.from(new Set([...lockedDays, fullDate]))
      : lockedDays.filter((d) => d !== fullDate);

    setLockedDays(newLocked);

    try {
      // ★変更点2: POSTパラメータを修正版PHPに合わせる
      // action: 'saveDayLock'
      // date: 'YYYY-MM-DD' (fullDateをそのまま送る)
      // is_locked: true/false
      
      const payload = {
        action: 'saveDayLock',
        user_id: userId,
        date: fullDate, // PHP側で分解するのでそのまま送る
        is_locked: lock
      };

      await axios.post(
        apiBaseRef.current,
        payload, // axiosはデフォルトでJSONとして送ります
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log(`ロック更新成功: ${fullDate} -> ${lock}`);
      
      // 念のため再取得（不要ならコメントアウト可）
      // await fetchLocks(); 

    } catch (err) {
      console.error("ロック更新エラー:", err);
      // ロールバック
      setLockedDays(prev);
      alert("ロック状態の更新に失敗しました。");
    }
  };

  const isLocked = (fullDate) => lockedDays.includes(fullDate);

  return { lockedDays, toggleLock, isLocked, loading, reload: fetchLocks };
}